import { Component, inject, OnInit, ViewChild, effect } from '@angular/core';
import { TableModule } from 'primeng/table';
import { AgpmService } from '../agpm.service';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { MultiSelectModule } from 'primeng/multiselect';
import { CommonModule } from '@angular/common';
import { TagModule } from 'primeng/tag';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { MenuModule } from 'primeng/menu';
import { Router } from '@angular/router';
import { PageHeaderComponent } from '../../../components/shared/page-header/page-header.component';
import { getCurrentNoticeDataTableStructure } from './utils/current-notice-data-table';
import { DataTableBuilderComponent } from '../../../components/data-table-builder/data-table-builder.component';
import { DataTableAction, DataTableRowActions, DynamicDataTable } from '../../../components/data-table-builder/data-table.types';
import { AgpmRepository } from '../../../store/agpm-state/agpm-state.repository';
import { Agpm, ResponseDoc, TasksStatus } from '@/types';
import { MessageService } from 'primeng/api';
import { DialogComponent } from '../../../components/dialog/dialog.component';
import { DialogObject } from '../../../components/dialog/dialog.types';
import { getAgpmDeleteObject } from './utils/agpm-delete-dialog-object';
import { TranslateService } from '@ngx-translate/core';
import { AuthorizationService } from '@/services/authorization.service';
import { createServerQuery } from '../../../components/data-table-builder/server-query';

@Component({
  selector: 'sm-agpm-current-notice',
  standalone: true,
  templateUrl: './current-notice.component.html',
  styleUrl: './current-notice.component.scss',
  imports: [
    TableModule,
    IconFieldModule,
    InputIconModule,
    MultiSelectModule,
    CommonModule,
    TagModule,
    ButtonModule,
    InputTextModule,
    MenuModule,
    PageHeaderComponent,
    DataTableBuilderComponent,
    DialogComponent,
  ],
})
export class AgpmCurrentNoticeComponent implements OnInit {
  agpmService = inject(AgpmService);
  messageService = inject(MessageService);
  agpmRepository = inject(AgpmRepository);
  router = inject(Router);
  translate = inject(TranslateService);
  authorizationService = inject(AuthorizationService);

  breadcrumb = [
    {
      label: 'agpm.plural',
      routerLink: ['agpm-current-notice'],
    },
  ];

  isRedactor = this.authorizationService.isRedacteur();
  isValidator = this.authorizationService.isValidator();
  isPublisher = this.authorizationService.isPublisher();

  rowActionsFn: (target: Agpm) => DataTableRowActions = (target: Agpm) => {
    const isDraft = target.status === TasksStatus.BROUILLON;
    const isOnProgress = target.status === TasksStatus.EN_TRAITEMENT;
    const isValidated = target.status === TasksStatus.VALIDE;
    const additionalActions: Record<string, DataTableAction[] | undefined> = {};
    if (target.processInstanceId)
      additionalActions['history'] = [
        {
          icon: 'pi pi-search',
          label: 'table.actions.accessTaskHistory',
          action: () => {
            this.router.navigate([`agpm-task-history`, target.processInstanceId]);
          },
        },
      ];
    return {
      defaultActions: {
        edit: {
          label: (this.isValidator && isOnProgress) || (this.isPublisher && isValidated) ? 'table.actions.treat' : 'table.actions.edit',
        },
      },
      inspectAction: () => {
        this.router.navigate([`agpm-inspect-notice/${this.agpmRepository.getNested('response', 'id')}`]);
      },
      editAction:
        (this.isRedactor && isDraft) || (this.isValidator && isOnProgress) || (this.isPublisher && isValidated)
          ? () => {
              this.router.navigate([`agpm-edit-notice/${this.agpmRepository.getNested('response', 'id')}`]);
              this.agpmRepository.setNested(
                'dto',
                'docIds',
                this.agpmRepository.getNested<ResponseDoc[]>('response', 'docs').map(doc => doc.id)
              );
            }
          : undefined,
      deleteAction:
        this.isRedactor && isDraft
          ? () => {
              this.agpmDialog = getAgpmDeleteObject({
                representation: this.agpmRepository.getNested('response', 'id'),
                action: () => {
                  this.deleteAgpm(this.agpmRepository.getNested('response', 'id'), this.agpmRepository.getNested('response', 'version'));
                  this.dialogComponent.toggleVisible();
                },
                toggleVisiblity: () => this.dialogComponent.toggleVisible(),
              });
              this.dialogComponent.toggleVisible();
            }
          : undefined,
      additionalActions: additionalActions,
    };
  };

  data: Agpm[] = [];

  serverQuery = createServerQuery({ initialPageSize: 10, initialSortBy: 'updatedAt', initialSortOrder: 'desc' });

  dataTableObject: DynamicDataTable = {
    ...getCurrentNoticeDataTableStructure({
      store: this.agpmRepository,
      rowActionsFn: this.rowActionsFn,
      createAction: this.isRedactor ? () => this.redirectNewCurrentNotice() : undefined,
    }),
    enableServerActions: true,
    serverQuery: this.serverQuery,
  };

  totalRecords = 0;
  loading = true;

  @ViewChild('dialog') dialogComponent!: DialogComponent;
  agpmDialog?: DialogObject;

  constructor() {
    effect(() => {
      const page = this.serverQuery.page();
      const size = this.serverQuery.pageSize();
      const sortBy = this.serverQuery.sortBy();
      const sortOrder = this.serverQuery.sortOrder();
      const search = this.serverQuery.search();
      const sort = sortBy ? `${sortBy},${sortOrder}` : 'updatedAt,desc';
      this.loadData(page, size, sort, search);
    });
  }

  ngOnInit(): void {
    this.agpmRepository.reset();
  }

  redirectNewCurrentNotice() {
    this.router.navigate(['/agpm-new-notice']);
  }

  deleteAgpm(id: number, version?: number) {
    return this.agpmService.deleteAgpm(id, version).subscribe({
      next: () => {
        this.loading = true;
        this.loadData(this.serverQuery.page(), this.serverQuery.pageSize(), undefined, this.serverQuery.search());
        this.messageService.add({
          severity: 'success',
          summary: 'Succès',
          detail: this.translate.instant('agpm.message.success.deleted', {
            reference: this.agpmRepository.getNested('response', 'ref'),
          }),
        });
      },
      error: err => {
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail:
            err.error.message ||
            this.translate.instant('agpm.message.error.deleted', {
              reference: this.agpmRepository.getNested('response', 'ref'),
            }),
        });
      },
    });
  }

  loadData(page = 0, size = 10, sort = 'updatedAt,desc', search?: string) {
    this.loading = true;
    this.agpmService.getAgpms({ page, size, sort, search }).subscribe({
      next: data => {
        this.data = data.content;
        this.totalRecords = data.totalElements;
        this.loading = false;
      },
      error: error => {
        if (error.status !== 401) {
          this.loading = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Erreur',
            detail: error.error.message || this.translate.instant('agpm.message.error.loading'),
          });
        } else {
          this.loading = false;
        }
      },
    });
  }
}
